/**
 * ListPageLayout
 *
 * Full-height page scaffold: header / toolbar are fixed, the body fills and scrolls
 * internally, and the footer (pager) stays pinned. This is the default page scaffold
 * for any list/grid page — master/detail layouts are NOT the default.
 * See `.github/context/list-grid-standard.context.md` §2.
 */
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';

const usePageStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalL,
  },
  fixed: {
    flexShrink: 0,
  },
  body: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
});

export interface ListPageLayoutProps {
  header?: ReactNode;
  toolbar?: ReactNode;
  footer?: ReactNode;
}

export const ListPageLayout: FC<PropsWithChildren<ListPageLayoutProps>> = ({
  header,
  toolbar,
  footer,
  children,
}) => {
  const styles = usePageStyles();
  return (
    <div className={styles.root}>
      {header && <div className={styles.fixed}>{header}</div>}
      {toolbar && <div className={styles.fixed}>{toolbar}</div>}
      <div className={styles.body}>{children}</div>
      {footer && <div className={styles.fixed}>{footer}</div>}
    </div>
  );
};
